<template>
  <div class="franquia-form-container">
    <!-- Edit mode info -->
    <div v-if="isEditMode && formData.ID" class="info-banner">
      <span class="info-text">
        <strong>ID:</strong> {{ formData.ID }} &bull;
        <strong>{{ t('Última alteração') }}:</strong> {{ formatDate(formData.UpdatedAt) }}
      </span>
    </div>

    <!-- Main Form Section -->
    <div class="form-section">
      <div class="form-grid">
        <!-- Nome -->
        <div class="form-field full-width" :class="{ 'has-error': errors.Nome }">
          <label>{{ t('Nome') }}</label>
          <input-text
            data-testid="franquias-form-nome"
            elem-id="inputFranquiaNome"
            :placeholder="t('Nome da franquia')"
            v-model="formData.Nome"
            @input="validateForm" />
          <span v-if="errors.Nome" class="field-error">{{ errors.Nome }}</span>
        </div>

        <!-- Email -->
        <div class="form-field" :class="{ 'has-error': errors.Email }">
          <label>{{ t('Email') }}</label>
          <input-text
            data-testid="franquias-form-email"
            elem-id="inputFranquiaEmail"
            type="email"
            :placeholder="t('Email da franquia')"
            v-model="formData.Email"
            @input="validateForm" />
          <span v-if="errors.Email" class="field-error">{{ errors.Email }}</span>
        </div>

        <!-- País -->
        <div class="form-field" :class="{ 'has-error': errors.Pais }">
          <label>{{ t('País') }}</label>
          <select-list
            data-testid="franquias-form-pais"
            elem-id="inputFranquiaPais"
            :placeholder="t('Escolha')"
            :itens-select="paisesFormatted"
            v-model="formData.Pais"
            @input="validateForm" />
          <span v-if="errors.Pais" class="field-error">{{ errors.Pais }}</span>
        </div>

        <!-- Documento (multi-país via InputDocument) -->
        <div class="form-field full-width">
          <div v-if="hasMultipleDocumentTypes" class="document-type-selector">
            <label class="field-label">{{ t('Tipo de Documento') }}</label>
            <div class="doc-type-options">
              <button
                v-for="opt in allowedDocumentOptions"
                :key="opt.type"
                type="button"
                class="doc-type-btn"
                :class="{ active: opt.type === selectedDocumentType }"
                :data-testid="`franquia-doctype-${opt.type}`"
                @click="setSelectedDocumentType(opt.type)">
                <span>{{ opt.flag }}</span>
                <span>{{ opt.label }}</span>
              </button>
            </div>
          </div>
          <InputDocument
            data-testid="franquias-form-documento"
            elem-id="inputFranquiaDocumento"
            :document-type="selectedDocumentType"
            v-model="formData.CNPJ"
            :label="t('Documento')"
            :ng-required="false"
            :show-country-flag="true"
            @validation="onDocumentValidation"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalidades } from '../../composables/useLocalidades';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useCustomerInternationalConfig } from '../../composables/useCustomerInternationalConfig';
import type { Franquias_SalvaFranquiaInput } from '../../contracts/Franquias_SalvaFranquiaInput';

type Id = number | string;

interface FranquiaInner {
  ID?: Id | null;
  Nome?: string;
  Email?: string;
  CNPJ?: string;
  Pais?: string;
  PaisNome?: string;
  Idioma?: string;
  UpdatedAt?: string | null;
  [key: string]: unknown;
}

interface FormData {
  ID: Id | null;
  Nome: string;
  Email: string;
  CNPJ: string;
  Pais: string;
  Idioma: string;
  UpdatedAt: string | null;
}

interface FormErrors {
  Nome: string | null;
  Email: string | null;
  Pais: string | null;
}

interface PaisItem {
  Name?: string;
  ID?: Id;
  Code?: string;
}

interface DocumentConfig {
  allowed_types: string[];
  default_type: string;
}

const { t } = useI18n();
const { fetchPaises } = useLocalidades();
const { formatDateTime: formatDateTimeI18n } = useDateFormatter();

// International document configuration
const {
  setCountryFromName,
  setDocumentConfig,
  selectedDocumentType,
  setSelectedDocumentType,
  currentCountry,
  allowedDocumentOptions,
  hasMultipleDocumentTypes,
} = useCustomerInternationalConfig();

interface Props {
  franquiaId?: number | null;
  isEditMode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaId: null,
  isEditMode: false,
});

const emit = defineEmits<{
  'form-valid': [isValid: boolean];
}>();

// Reactive data
const formData = reactive<FormData>({
  ID: null,
  Nome: '',
  Email: '',
  CNPJ: '',
  Pais: '',
  Idioma: 'PTBR', // Default language
  UpdatedAt: null,
});

const errors = reactive<FormErrors>({
  Nome: null,
  Email: null,
  Pais: null,
});

const paises = ref<PaisItem[]>([]);

// ===== DOCUMENTO COM SUPORTE MULTI-PAÍS (via InputDocument) =====

// Validação do documento gerenciada pelo InputDocument
const documentValid = ref(true);
const documentInvalid = ref(false);

const onDocumentValidation = (result: { valid: boolean; error: string | null }): void => {
  documentValid.value = result.valid;
  documentInvalid.value = !result.valid;
  validateForm();
};

// Format paises for SelectList component (expects {ID, Name} format).
// ID is the ISO code (pais.Code) — the select writes the CODE into
// formData.Pais while still displaying the resolved Name (S1b: storage must
// move to ISO, never re-write the free-text name on save).
const paisesFormatted = computed(() => {
  return paises.value.map((pais) => ({
    ID: pais.Code || pais.Name || pais.ID,
    Name: pais.Name || pais.ID,
  }));
});

// Resolve a franquia.Pais value (either storage state) to the Code that
// paisesFormatted's options use as ID (S1B-04). Without this, a franchise
// still on the PRE-migration name state ('Brasil') never matches any
// option (whose ID is now the Code), so the SelectList renders blank even
// though formData.Pais holds a real value. Idempotent: an already-Code
// value simply matches on the first pass and is returned unchanged.
const resolvePaisToCode = (rawPais: string): string => {
  if (!rawPais || paises.value.length === 0) {
    return rawPais;
  }
  const byCode = paises.value.find((pais) => pais.Code === rawPais);
  if (byCode) {
    return rawPais;
  }
  const byName = paises.value.find((pais) => pais.Name === rawPais);
  return byName?.Code || rawPais;
};

// Methods
const loadPaises = async (): Promise<void> => {
  try {
    paises.value = (await fetchPaises()) as PaisItem[];
    // paises may finish loading AFTER loadData() already ran (openForEdit
    // awaits the franquia fetch, not this one) — re-resolve once the
    // registry is available so the select still shows the right option.
    if (formData.Pais) {
      formData.Pais = resolvePaisToCode(formData.Pais);
    }
  } catch {
    // Error loading paises
  }
};

/**
 * Configura documentação baseada no país detectado
 * Mesma lógica do Wizard de Franquias
 */
const loadDocumentConfig = (): void => {
  const countryCode = currentCountry.value || 'BR';

  // Configurações por país - mesmas do Wizard
  const countryConfigs: Record<string, DocumentConfig> = {
    'BR': {
      allowed_types: ['cnpj'],
      default_type: 'cnpj'
    },
    'CL': {
      allowed_types: ['rut'],
      default_type: 'rut'
    },
    'AR': {
      allowed_types: ['dni'],
      default_type: 'dni'
    },
    'BO': {
      allowed_types: ['ci'],
      default_type: 'ci'
    },
    'US': {
      allowed_types: ['ssn'],
      default_type: 'ssn'
    },
    'PT': {
      allowed_types: ['nif'],
      default_type: 'nif'
    },
    'MX': {
      allowed_types: ['curp'],
      default_type: 'curp'
    },
    'GT': {
      allowed_types: ['dpi'],
      default_type: 'dpi'
    },
    'PY': {
      allowed_types: ['cedula'],
      default_type: 'cedula'
    },
    'EC': {
      allowed_types: ['cedula_ec', 'ruc'],
      default_type: 'cedula_ec'
    }
  };

  // Obter configuração baseada no país detectado
  const config = countryConfigs[countryCode] || countryConfigs['BR'];

  // Seta a configuração de documentos
  setDocumentConfig({
    document_types: config.allowed_types,
    default_document_type: config.default_type,
  });
};

const loadData = (franquia: FranquiaInner | undefined): void => {
  if (franquia) {
    formData.ID = franquia.ID || null;
    formData.Nome = franquia.Nome || '';
    formData.Email = franquia.Email || '';
    formData.CNPJ = franquia.CNPJ || '';
    formData.Pais = resolvePaisToCode(franquia.Pais || '');
    formData.Idioma = franquia.Idioma || 'PTBR';
    formData.UpdatedAt = franquia.UpdatedAt || null;

    // Detect country for international document
    const paisNome = franquia.Pais || franquia.PaisNome;
    if (paisNome) {
      setCountryFromName(paisNome);
      loadDocumentConfig();
    }

    // InputDocument gerencia formatação, máscara e validação
    documentValid.value = true;
    documentInvalid.value = false;

    validateForm();
  }
};

const validateForm = (): boolean => {
  // Reset errors
  errors.Nome = null;
  errors.Email = null;
  errors.Pais = null;

  let isValid = true;

  // Validate Nome
  if (!formData.Nome || formData.Nome.trim() === '') {
    errors.Nome = t('Nome é obrigatório');
    isValid = false;
  }

  // Validate Email
  if (!formData.Email || formData.Email.trim() === '') {
    errors.Email = t('Email é obrigatório');
    isValid = false;
  } else if (!isValidEmail(formData.Email)) {
    errors.Email = t('Email inválido');
    isValid = false;
  }

  // Validate Pais
  if (!formData.Pais || formData.Pais.trim() === '') {
    errors.Pais = t('País é obrigatório');
    isValid = false;
  }

  // Validate Documento (multi-país — validado pelo InputDocument)
  if (formData.CNPJ && formData.CNPJ.trim().length > 0 && !documentValid.value) {
    isValid = false;
  }

  emit('form-valid', isValid);
  return isValid;
};

const isValidEmail = (email: string): boolean => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const getFormData = (): Franquias_SalvaFranquiaInput => {
  const data: Franquias_SalvaFranquiaInput = {
    Nome: formData.Nome,
    Email: formData.Email,
    CNPJ: formData.CNPJ ?? undefined,
    Pais: formData.Pais ?? undefined,
    Idioma: formData.Idioma ?? undefined,
  };

  // Only include ID if in edit mode
  if (props.isEditMode && formData.ID) {
    data.ID = Number(formData.ID);
  }

  return data;
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return formatDateTimeI18n(date);
  } catch {
    return dateString;
  }
};

// Watch for changes
watch(() => props.franquiaId, () => {
  // Reset form when franquiaId changes
  if (!props.franquiaId) {
    formData.ID = null;
    formData.Nome = '';
    formData.Email = '';
    formData.CNPJ = '';
    formData.Pais = '';
    formData.Idioma = 'PTBR';
    formData.UpdatedAt = null;

    // Reset documento
    documentValid.value = true;
    documentInvalid.value = false;

    validateForm();
  }
});

// Watch for country changes to update document type
watch(() => formData.Pais, (newPais) => {
  if (newPais) {
    setCountryFromName(newPais);
    // Carregar configuração do documento para o país selecionado
    loadDocumentConfig();
  }
});

// Lifecycle
onMounted(() => {
  loadPaises();
  validateForm();
});

// Expose methods for parent
defineExpose({
  loadData,
  getFormData,
  validateForm
});
</script>

<style scoped>
.franquia-form-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Info Banner (Edit Mode) */
.info-banner {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  font-size: 13px;
}

.info-text {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: var(--text-color);
}

/* Form Section */
.form-section {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--surface-border);
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field label {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-color-secondary);
}

/* Document type selector */
.document-type-selector {
  margin-bottom: 8px;
}

.document-type-selector .field-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.doc-type-options {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.doc-type-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-card);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.15s ease;
}

.doc-type-btn:hover {
  border-color: var(--primary-color);
  background: var(--primary-50);
}

.doc-type-btn.active {
  border-color: var(--primary-color);
  background: var(--primary-100);
  color: var(--primary-color);
  font-weight: 600;
}

.form-field :deep(.input-text-wrapper),
.form-field :deep(.select-list-wrapper) {
  width: 100%;
}

/* Field modifiers */
.form-field.full-width {
  grid-column: 1 / -1;
}

.form-field.has-error :deep(.input-text-wrapper),
.form-field.has-error :deep(.select-list-wrapper) {
  border-color: #ef4444;
}

.form-field.has-error :deep(.input-text-wrapper):focus-within {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.form-field.has-success :deep(.input-text-wrapper) {
  border-color: #22c55e;
}

.form-field.has-success :deep(.input-text-wrapper):focus-within {
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
}

.field-error {
  font-size: 12px;
  color: #ef4444;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .info-text {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}
</style>
